
import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiCheckCircle,
    FiClock,
    FiFileText,
    FiLoader,
    FiPrinter,
    FiSlash,
    FiX,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    cancelCreditNote,
    confirmCreditNote,
    fetchCreditNoteById,
    selectedCreditNoteSelector,
    creditNoteLoadingSelector,
    creditNoteErrorSelector,
    clearSelectedCreditNote,
} from "../../../../../../../store/billing/creditNotes/creditNoteSlice";

import "./CreditNoteDetailsDialog.css";

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(number)
        ? number
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(value) +
            Number.EPSILON
        ) *
            100
    ) / 100;

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    ).format(
        roundMoney(value)
    );

const formatDate = (
    value,
    includeTime = false
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        includeTime
            ? {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
              }
            : {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
              }
    ).format(date);
};

const formatStatus = (
    value
) =>
    String(value || "-")
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(
            /\b\w/g,
            (character) =>
                character.toUpperCase()
        );

const getCreditNoteId = (
    creditNote
) =>
    creditNote?._id ||
    creditNote?.id ||
    "";

const getCreditAmount = (
    creditNote
) =>
    roundMoney(
        creditNote
            ?.totals
            ?.creditAmount ??
        creditNote
            ?.creditAmount ??
        0
    );

const getErrorMessage = (
    error,
    fallback
) =>
    error?.message ||
    error?.payload?.message ||
    error ||
    fallback;

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const CreditNoteDetailsDialog = ({
    open,
    creditNote,
    onClose,
    onUpdated,
}) => {
    const dispatch =
        useDispatch();

    const selectedCreditNote =
        useSelector(
            selectedCreditNoteSelector
        );

    const loading =
        useSelector(
            creditNoteLoadingSelector
        ) || {};

    const errors =
        useSelector(
            creditNoteErrorSelector
        ) || {};

    const creditNoteId =
        getCreditNoteId(
            creditNote
        );

    const currentCreditNote =
        selectedCreditNote?._id ===
        creditNoteId
            ? selectedCreditNote
            : creditNote;

    const [
        localError,
        setLocalError,
    ] = useState("");

    const [
        showCancelForm,
        setShowCancelForm,
    ] = useState(false);

    const [
        cancellationReason,
        setCancellationReason,
    ] = useState("");

    const status =
        String(
            currentCreditNote
                ?.status ||
                "DRAFT"
        ).toUpperCase();

    const items =
        useMemo(
            () =>
                Array.isArray(
                    currentCreditNote
                        ?.items
                )
                    ? currentCreditNote.items
                    : [],
            [
                currentCreditNote,
            ]
        );

    const processing =
        Boolean(
            loading.details ||
            loading.confirm ||
            loading.cancel
        );

    /*
    |--------------------------------------------------------------------------
    | LOAD COMPLETE DETAILS
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            if (
                !open ||
                !creditNoteId
            ) {
                return;
            }

            setLocalError("");
            setShowCancelForm(false);
            setCancellationReason("");

            dispatch(
                fetchCreditNoteById(
                    creditNoteId
                )
            );
        },
        [
            dispatch,
            open,
            creditNoteId,
        ]
    );

    /*
    |--------------------------------------------------------------------------
    | CLOSE
    |--------------------------------------------------------------------------
    */

    const handleClose = () => {
        if (processing) {
            return;
        }

        dispatch(
            clearSelectedCreditNote()
        );

        setLocalError("");
        setShowCancelForm(false);
        setCancellationReason("");

        onClose?.();
    };

    /*
    |--------------------------------------------------------------------------
    | CONFIRM
    |--------------------------------------------------------------------------
    */

    const handleConfirm =
        async () => {
            if (
                !creditNoteId
            ) {
                return;
            }

            const approved =
                window.confirm(
                    `Confirm credit note ${
                        currentCreditNote
                            ?.creditNoteNumber ||
                        ""
                    }?\n\nStock and customer ledger will be updated.`
                );

            if (!approved) {
                return;
            }

            try {
                setLocalError("");

                const updated =
                    await dispatch(
                        confirmCreditNote(
                            creditNoteId
                        )
                    ).unwrap();

                if (
                    typeof onUpdated ===
                    "function"
                ) {
                    await onUpdated(
                        updated
                    );
                }
            } catch (error) {
                setLocalError(
                    getErrorMessage(
                        error,
                        "Unable to confirm credit note."
                    )
                );
            }
        };

    /*
    |--------------------------------------------------------------------------
    | CANCEL
    |--------------------------------------------------------------------------
    */

    const handleCancel =
        async () => {
            const reason =
                cancellationReason.trim();

            if (
                reason.length < 3
            ) {
                setLocalError(
                    "Cancellation reason must contain at least 3 characters."
                );

                return;
            }

            try {
                setLocalError("");

                const updated =
                    await dispatch(
                        cancelCreditNote({
                            creditNoteId,
                            cancellationReason:
                                reason,
                        })
                    ).unwrap();

                setShowCancelForm(false);
                setCancellationReason("");

                if (
                    typeof onUpdated ===
                    "function"
                ) {
                    await onUpdated(
                        updated
                    );
                }
            } catch (error) {
                setLocalError(
                    getErrorMessage(
                        error,
                        "Unable to cancel credit note."
                    )
                );
            }
        };

    /*
    |--------------------------------------------------------------------------
    | PRINT
    |--------------------------------------------------------------------------
    */

    const handlePrint = () => {
        /*
        |--------------------------------------------------------------------------
        | PRINT ONLY CREDIT NOTE DIALOG
        |--------------------------------------------------------------------------
        |
        | Do not print the surrounding admin layout/sidebar.
        | We clone only the credit-note dialog into a temporary print root,
        | then invoke the browser's native print dialog.
        |
        | No new browser tab/window is opened.
        |--------------------------------------------------------------------------
        */

        const sourceElement =
            document.querySelector(
                ".credit-note-details-dialog"
            );

        if (
            !sourceElement
        ) {
            return;
        }


        const printRootId =
            "credit-note-details-print-root";


        const existingPrintRoot =
            document.getElementById(
                printRootId
            );


        if (
            existingPrintRoot
        ) {
            existingPrintRoot.remove();
        }


        const printRoot =
            document.createElement(
                "div"
            );


        printRoot.id =
            printRootId;


        const printContent =
            sourceElement.cloneNode(
                true
            );


        /*
        |--------------------------------------------------------------------------
        | REMOVE SCREEN-ONLY CONTROLS
        |--------------------------------------------------------------------------
        */

        printContent
            .querySelectorAll(
                [
                    ".credit-note-details-close",
                    ".credit-note-details-footer",
                    ".credit-note-details-cancel-form",
                ].join(",")
            )
            .forEach(
                (
                    element
                ) => {
                    element.remove();
                }
            );


        printRoot.appendChild(
            printContent
        );


        document.body.appendChild(
            printRoot
        );


        let cleaned =
            false;


        const cleanupPrintRoot =
            () => {
                if (
                    cleaned
                ) {
                    return;
                }


                cleaned =
                    true;


                const root =
                    document.getElementById(
                        printRootId
                    );


                if (
                    root
                ) {
                    root.remove();
                }


                window.removeEventListener(
                    "afterprint",
                    cleanupPrintRoot
                );
            };


        window.addEventListener(
            "afterprint",
            cleanupPrintRoot
        );


        /*
         * Wait for one paint cycle so the print-only CSS is applied
         * before opening native print preview.
         */
        window.requestAnimationFrame(
            () => {
                window.requestAnimationFrame(
                    () => {
                        window.print();


                        /*
                         * Fallback cleanup for browsers that do not emit
                         * afterprint after cancelling preview.
                         */
                        window.setTimeout(
                            cleanupPrintRoot,
                            2000
                        );
                    }
                );
            }
        );
    };

    if (!open) {
        return null;
    }

    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <div
            className="credit-note-details-overlay"
            role="presentation"
        >
            <div
                className="credit-note-details-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="credit-note-details-title"
            >
                <header className="credit-note-details-header">
                    <div className="credit-note-details-heading">
                        <span className="credit-note-details-heading-icon">
                            <FiFileText />
                        </span>

                        <div>
                            <h2 id="credit-note-details-title">
                                {currentCreditNote
                                    ?.creditNoteNumber ||
                                    "Credit Note"}
                            </h2>

                            <p>
                                Against invoice{" "}
                                <strong>
                                    {currentCreditNote
                                        ?.saleInvoiceNumber ||
                                        "-"}
                                </strong>
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="credit-note-details-close"
                        disabled={
                            processing
                        }
                        onClick={
                            handleClose
                        }
                        aria-label="Close"
                    >
                        <FiX />
                    </button>
                </header>

                <div className="credit-note-details-body">
                    {(localError ||
                        errors.details ||
                        errors.confirm ||
                        errors.cancel) && (
                        <div className="credit-note-details-alert">
                            <FiAlertCircle />

                            <span>
                                {localError ||
                                    errors.details ||
                                    errors.confirm ||
                                    errors.cancel}
                            </span>
                        </div>
                    )}

                    {loading.details &&
                    !currentCreditNote
                        ? (
                            <div className="credit-note-details-loading">
                                <FiLoader className="credit-note-details-spinner" />

                                Loading credit note...
                            </div>
                        )
                        : (
                            <>
                                <section className="credit-note-details-summary-grid">
                                    <div>
                                        <span>
                                            Credit Note Date
                                        </span>

                                        <strong>
                                            {formatDate(
                                                currentCreditNote
                                                    ?.creditNoteDate
                                            )}
                                        </strong>
                                    </div>

                                    <div>
                                        <span>
                                            Customer
                                        </span>

                                        <strong>
                                            {currentCreditNote
                                                ?.customerSnapshot
                                                ?.customerName ||
                                                "Walk-in Customer"}
                                        </strong>
                                    </div>

                                    <div>
                                        <span>
                                            Reason
                                        </span>

                                        <strong>
                                            {formatStatus(
                                                currentCreditNote
                                                    ?.reason
                                            )}
                                        </strong>
                                    </div>

                                    <div>
                                        <span>
                                            Status
                                        </span>

                                        <strong
                                            className={`credit-note-details-status credit-note-details-status-${status.toLowerCase()}`}
                                        >
                                            {status ===
                                            "CONFIRMED" ? (
                                                <FiCheckCircle />
                                            ) : status ===
                                              "CANCELLED" ? (
                                                <FiSlash />
                                            ) : (
                                                <FiClock />
                                            )}

                                            {formatStatus(
                                                status
                                            )}
                                        </strong>
                                    </div>
                                </section>

                                {currentCreditNote
                                    ?.reasonDetails && (
                                    <section className="credit-note-details-card">
                                        <h3>
                                            Reason Details
                                        </h3>

                                        <p>
                                            {
                                                currentCreditNote
                                                    .reasonDetails
                                            }
                                        </p>
                                    </section>
                                )}

                                <section className="credit-note-details-card">
                                    <h3>
                                        Returned Items
                                    </h3>

                                    <div className="credit-note-details-table-wrapper">
                                        <table className="credit-note-details-table">
                                            <thead>
                                                <tr>
                                                    <th>
                                                        #
                                                    </th>

                                                    <th>
                                                        Item
                                                    </th>

                                                    <th className="credit-note-details-text-right">
                                                        Return Qty
                                                    </th>

                                                    <th className="credit-note-details-text-right">
                                                        Rate
                                                    </th>

                                                    <th className="credit-note-details-text-right">
                                                        Discount
                                                    </th>

                                                    <th className="credit-note-details-text-right">
                                                        Tax
                                                    </th>

                                                    <th className="credit-note-details-text-right">
                                                        Amount
                                                    </th>

                                                    <th>
                                                        Stock
                                                    </th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {items.map(
                                                    (
                                                        item,
                                                        index
                                                    ) => (
                                                        <tr
                                                            key={
                                                                item?._id ||
                                                                index
                                                            }
                                                        >
                                                            <td>
                                                                {index +
                                                                    1}
                                                            </td>

                                                            <td>
                                                                <strong>
                                                                    {item
                                                                        ?.itemName ||
                                                                        "-"}
                                                                </strong>

                                                                <span>
                                                                    {item
                                                                        ?.description ||
                                                                        item
                                                                            ?.hsnSac ||
                                                                        ""}
                                                                </span>
                                                            </td>

                                                            <td className="credit-note-details-text-right">
                                                                {toNumber(
                                                                    item
                                                                        ?.returnQuantity
                                                                )}
                                                            </td>

                                                            <td className="credit-note-details-text-right">
                                                                {formatCurrency(
                                                                    item
                                                                        ?.rate
                                                                )}
                                                            </td>

                                                            <td className="credit-note-details-text-right">
                                                                {formatCurrency(
                                                                    item
                                                                        ?.discountAmount
                                                                )}
                                                            </td>

                                                            <td className="credit-note-details-text-right">
                                                                {formatCurrency(
                                                                    item
                                                                        ?.taxAmount
                                                                )}
                                                            </td>

                                                            <td className="credit-note-details-text-right credit-note-details-item-total">
                                                                {formatCurrency(
                                                                    item
                                                                        ?.totalAmount
                                                                )}
                                                            </td>

                                                            <td>
                                                                {item
                                                                    ?.restoreStock
                                                                    ? item
                                                                          ?.stockRestored
                                                                        ? "Restored"
                                                                        : "Pending"
                                                                    : "Not Required"}
                                                            </td>
                                                        </tr>
                                                    )
                                                )}

                                                {!items.length && (
                                                    <tr>
                                                        <td
                                                            colSpan="8"
                                                            className="credit-note-details-empty"
                                                        >
                                                            No returned items found.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </section>

                                <div className="credit-note-details-bottom-grid">
                                    <section className="credit-note-details-card">
                                        <h3>
                                            Notes
                                        </h3>

                                        <p>
                                            {currentCreditNote
                                                ?.notes ||
                                                "No notes added."}
                                        </p>

                                        {status ===
                                            "CANCELLED" && (
                                            <div className="credit-note-details-cancellation">
                                                <strong>
                                                    Cancellation Reason
                                                </strong>

                                                <span>
                                                    {currentCreditNote
                                                        ?.cancellationReason ||
                                                        "-"}
                                                </span>

                                                <small>
                                                    {formatDate(
                                                        currentCreditNote
                                                            ?.cancelledAt,
                                                        true
                                                    )}
                                                </small>
                                            </div>
                                        )}
                                    </section>

                                    <section className="credit-note-details-card credit-note-details-total-card">
                                        <h3>
                                            Credit Summary
                                        </h3>

                                        <div>
                                            <span>
                                                Gross Amount
                                            </span>

                                            <strong>
                                                {formatCurrency(
                                                    currentCreditNote
                                                        ?.totals
                                                        ?.grossAmount
                                                )}
                                            </strong>
                                        </div>

                                        <div>
                                            <span>
                                                Discount
                                            </span>

                                            <strong>
                                                -
                                                {formatCurrency(
                                                    currentCreditNote
                                                        ?.totals
                                                        ?.discountAmount
                                                )}
                                            </strong>
                                        </div>

                                        <div>
                                            <span>
                                                Taxable Amount
                                            </span>

                                            <strong>
                                                {formatCurrency(
                                                    currentCreditNote
                                                        ?.totals
                                                        ?.taxableAmount
                                                )}
                                            </strong>
                                        </div>

                                        <div>
                                            <span>
                                                Tax
                                            </span>

                                            <strong>
                                                {formatCurrency(
                                                    currentCreditNote
                                                        ?.totals
                                                        ?.taxAmount
                                                )}
                                            </strong>
                                        </div>

                                        <div>
                                            <span>
                                                Round Off
                                            </span>

                                            <strong>
                                                {formatCurrency(
                                                    currentCreditNote
                                                        ?.totals
                                                        ?.roundOffAmount
                                                )}
                                            </strong>
                                        </div>

                                        <div className="credit-note-details-grand-total">
                                            <span>
                                                Credit Amount
                                            </span>

                                            <strong>
                                                {formatCurrency(
                                                    getCreditAmount(
                                                        currentCreditNote
                                                    )
                                                )}
                                            </strong>
                                        </div>
                                    </section>
                                </div>

                                {showCancelForm &&
                                    status ===
                                        "DRAFT" && (
                                    <section className="credit-note-details-cancel-form">
                                        <label>
                                            <span>
                                                Cancellation Reason
                                            </span>

                                            <textarea
                                                rows="3"
                                                maxLength="500"
                                                value={
                                                    cancellationReason
                                                }
                                                disabled={
                                                    processing
                                                }
                                                placeholder="Enter cancellation reason"
                                                onChange={(
                                                    event
                                                ) =>
                                                    setCancellationReason(
                                                        event
                                                            .target
                                                            .value
                                                    )
                                                }
                                            />
                                        </label>

                                        <div>
                                            <button
                                                type="button"
                                                disabled={
                                                    processing
                                                }
                                                onClick={() => {
                                                    setShowCancelForm(
                                                        false
                                                    );

                                                    setCancellationReason(
                                                        ""
                                                    );
                                                }}
                                            >
                                                Back
                                            </button>

                                            <button
                                                type="button"
                                                className="credit-note-details-confirm-cancel-button"
                                                disabled={
                                                    processing ||
                                                    cancellationReason
                                                        .trim()
                                                        .length <
                                                        3
                                                }
                                                onClick={
                                                    handleCancel
                                                }
                                            >
                                                {loading.cancel ? (
                                                    <FiLoader className="credit-note-details-spinner" />
                                                ) : (
                                                    <FiSlash />
                                                )}

                                                Cancel Credit Note
                                            </button>
                                        </div>
                                    </section>
                                )}
                            </>
                        )}
                </div>

                <footer className="credit-note-details-footer">
                    <button
                        type="button"
                        className="credit-note-details-button credit-note-details-close-button"
                        disabled={
                            processing
                        }
                        onClick={
                            handleClose
                        }
                    >
                        Close
                    </button>

                    <button
                        type="button"
                        className="credit-note-details-button credit-note-details-print-button"
                        disabled={
                            processing
                        }
                        onClick={
                            handlePrint
                        }
                    >
                        <FiPrinter />

                        Print
                    </button>

                    {status ===
                        "DRAFT" &&
                        !showCancelForm && (
                            <>
                                <button
                                    type="button"
                                    className="credit-note-details-button credit-note-details-cancel-button"
                                    disabled={
                                        processing
                                    }
                                    onClick={() => {
                                        setLocalError(
                                            ""
                                        );

                                        setCancellationReason(
                                            ""
                                        );

                                        setShowCancelForm(
                                            true
                                        );
                                    }}
                                >
                                    <FiSlash />

                                    Cancel Draft
                                </button>

                                <button
                                    type="button"
                                    className="credit-note-details-button credit-note-details-confirm-button"
                                    disabled={
                                        processing
                                    }
                                    onClick={
                                        handleConfirm
                                    }
                                >
                                    {loading.confirm ? (
                                        <FiLoader className="credit-note-details-spinner" />
                                    ) : (
                                        <FiCheckCircle />
                                    )}

                                    Confirm Credit Note
                                </button>
                            </>
                        )}
                </footer>
            </div>
        </div>
    );
};

export default CreditNoteDetailsDialog;